import {
  GPT_PALETTE_FROM_PROMPT_VERSION,
  GPT_PROMPT_VERSION_LATEST,
  MAIN_COLORS_NAMES,
} from './constants';
import {
  getFullPalettes,
  createGptLogoParams,
  fetchExtractColorsFromImage,
  fetchGenareteFromImageV2,
  getGptPromptsVersion,
  fetchColorFromPromptV2,
  getColorsPaletteAndLinkedPalette,
  getDependentLinksToUpdate,
  getDependentHexToUpdate,
  getLinkedHexToUpdate,
  fetchGenareteFromImageAndCurrentPalette,
  getFullPalettesBasedOnLinkedColors,
} from './utils';
import {
  ColorName,
  GptPalette,
  LinkedColorValue,
  PartialColorPalette,
} from './types';
import { convertColorPaletteToGPTParam } from '../colors/utils';
import { ColorPalette } from '@wix/document-services-types';

export const getColorsFromImage = async ({
  imagePath,
  metaSiteInstanceId,
  selectedGptPromptsVersion = GPT_PROMPT_VERSION_LATEST,
  featureName,
}: {
  imagePath: string;
  metaSiteInstanceId: string;
  selectedGptPromptsVersion?: string;
  featureName?: string;
}): Promise<{
  fullPalettes: PartialColorPalette[];
  gptPromptsVersion: string;
}> => {
  const colorsFromColorExtractor = await fetchExtractColorsFromImage(imagePath);
  if (!colorsFromColorExtractor) {
    throw new Error('No colors found in image');
  }

  const gptPromptsVersion =
    selectedGptPromptsVersion || getGptPromptsVersion(colorsFromColorExtractor);
  const gptParamsData = createGptLogoParams(colorsFromColorExtractor);

  let gptResponse;
  let gptPalettes;

  const userRequestInfo = featureName ? { featureName } : undefined;
  gptResponse = await fetchGenareteFromImageV2(
    metaSiteInstanceId,
    gptParamsData,
    userRequestInfo,
  );
  gptPalettes = JSON.parse(gptResponse.data.response.generatedTexts);

  const fullPalettes = getFullPalettes(gptPalettes, MAIN_COLORS_NAMES);

  return { fullPalettes, gptPromptsVersion };
};

export const getColorsFromPrompt = async ({
  currentPalette,
  userRequestMessage,
  metaSiteInstanceId,
  selectedGptPromptsVersion = GPT_PALETTE_FROM_PROMPT_VERSION,
  featureName,
}: {
  currentPalette: PartialColorPalette;
  metaSiteInstanceId: string;
  userRequestMessage: string;
  selectedGptPromptsVersion?: string;
  featureName?: string;
}): Promise<{
  fullPalettes: PartialColorPalette[];
  gptPromptsVersion: string;
}> => {
  const gptPromptsVersion = selectedGptPromptsVersion;
  const currentColors = convertColorPaletteToGPTParam(currentPalette);
  if (!currentColors) {
    throw new Error('No colors found, Failed convertColorPaletteToGPTParam');
  }
  let gptData;
  let gptPalettes;

  const requestParams = {
    CURRENT_USER_PALETTE: currentColors,
    USER_REQUEST_MESSAGE: userRequestMessage,
  };

  const userRequestInfo = featureName ? { featureName } : undefined;
  gptData = await fetchColorFromPromptV2(
    metaSiteInstanceId,
    requestParams,
    userRequestInfo,
  );
  gptPalettes = JSON.parse(gptData.data.response.generatedTexts);

  const fullPalettes = getFullPalettes(gptPalettes, MAIN_COLORS_NAMES);

  return { fullPalettes, gptPromptsVersion };
};

export const getKitAndLogoMixedPalette = (
  currentMainPalette: ColorPalette,
  currentLinkedPalette: Partial<
    Record<keyof ColorPalette, LinkedColorValue> | undefined
  >,
  promptPalette: Partial<ColorPalette>,
) => {
  const {
    updateMainColorFromPromptPalette,
    updateLinkedColorFromPromptPalette,
  } = getColorsPaletteAndLinkedPalette(promptPalette);
  const dependentLinksToUpdate = getDependentLinksToUpdate(
    updateLinkedColorFromPromptPalette,
    currentLinkedPalette,
  );
  const allLinksToUpdate = {
    ...currentLinkedPalette,
    ...updateLinkedColorFromPromptPalette,
    ...dependentLinksToUpdate,
  };
  const dependentHexToUpdate = getDependentHexToUpdate(
    updateMainColorFromPromptPalette,
    allLinksToUpdate,
  );
  const hexUpdates = {
    ...updateMainColorFromPromptPalette,
    ...dependentHexToUpdate,
  };
  const linkedHexToUpdate = getLinkedHexToUpdate(
    updateLinkedColorFromPromptPalette,
    dependentLinksToUpdate,
    hexUpdates,
    currentMainPalette,
  );
  return {
    colorSiteTheme: { ...hexUpdates, ...linkedHexToUpdate },
    linkedColorSiteTheme: { ...allLinksToUpdate },
  };
};

const jsonParsing = <T>(stringToParse: string) => {
  try {
    const jsonValue: T = JSON.parse(stringToParse);
    return jsonValue;
  } catch (e) {
    throw new Error(`Invalid JSON: ${stringToParse}`);
  }
};

export const getColorsFromImageAndCurrentPalette = async ({
  imagePath,
  currentColorsPalette,
  currentLinkedColorsPalette = {},
  metaSiteInstanceId,
  appDefId,
  promptIdToUse,
  featureName,
}: {
  imagePath: string;
  currentColorsPalette: PartialColorPalette;
  currentLinkedColorsPalette: Partial<Record<ColorName, LinkedColorValue>>;
  metaSiteInstanceId: string;
  appDefId: string | undefined;
  promptIdToUse: string;
  featureName?: string;
}): Promise<{
  fullPalettes: PartialColorPalette[];
}> => {
  if (!appDefId) {
    throw new Error('must provide an appDefId');
  }
  const colorsFromColorExtractor = await fetchExtractColorsFromImage(imagePath);
  if (!colorsFromColorExtractor) {
    throw new Error('No colors found in image');
  }
  const colorsFromImageToGpt = createGptLogoParams(colorsFromColorExtractor);
  const currentColors = convertColorPaletteToGPTParam(
    currentColorsPalette,
    false,
  );
  if (!currentColors) {
    throw new Error('No colors found, Failed convertColorPaletteToGPTParam');
  }

  const gptResponse = await fetchGenareteFromImageAndCurrentPalette(
    metaSiteInstanceId,
    appDefId,
    colorsFromImageToGpt,
    currentColors,
    promptIdToUse,
    featureName,
  );
  const gptPalette = jsonParsing<GptPalette>(
    gptResponse.data.response.generatedTexts,
  );

  const fullPalettes = getFullPalettesBasedOnLinkedColors(
    [gptPalette],
    currentLinkedColorsPalette,
    MAIN_COLORS_NAMES,
  );

  return { fullPalettes };
};
